import React, { useState } from "react";
import { motion } from "framer-motion";
import { Routes, Route, Navigate } from "react-router-dom";
import { Reveal } from "@/components/Reveal";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { FloatingWhatsApp } from "@/components/site/FloatingWhatsApp";
import { ScrollManager } from "@/components/site/ScrollManager";
import { REVIEWS, REVIEW_COLORS } from "@/lib/reviews";
import ProductLanding from "@/pages/ProductLanding";
import ContactPage from "@/pages/ContactPage";
import TrackOrderPage from "@/pages/TrackOrderPage";
import PolicyPage from "@/pages/PolicyPage";
import { HONEY_PAGE, GHEE_PAGE } from "@/lib/productPages";
import { POLICIES } from "@/lib/policies";
import { useSeo } from "@/lib/seo";
import { graph, webPageLd } from "@/lib/schema";
import { Link } from "react-router-dom";
import { fetchProductByTitle, addLineToSharedCart, applyDiscountCode } from "@/lib/shopify";
import {
  Menu, X, Leaf, ShieldCheck, Sparkles, Sun, Wheat, Package,
  Star, MessageCircle, Instagram, Facebook, Mail, Phone, MapPin,
  ChevronRight, Plus, ShoppingBag, Check, Heart, Award, Truck,
  Flame, Trees, Droplets, Send,
} from "lucide-react";
import axios from "axios";
import { toast, Toaster } from "sonner";
import { BRAND, ASSETS, TESTIDS, waUrl } from "@/lib/brand";
import ShopifyProductCard from "@/components/ShopifyProductCard";
import ComboOfferCard from "@/components/ComboOfferCard";
import "@/App.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;


/* -------------------- HERO -------------------- */
const Hero = () => (
  <section id="home" className="relative pt-[76px] sm:pt-32 lg:pt-36 pb-10 lg:pb-24 overflow-hidden bg-hero-gradient">
    {/* Decorative leaves */}
    <div className="absolute top-32 left-4 lg:left-16 opacity-40 pointer-events-none floaty-slow">
      <Leaf className="w-16 h-16 lg:w-24 lg:h-24 text-[#2E7D32]" strokeWidth={1} />
    </div>
    <div className="absolute bottom-24 right-6 lg:right-24 opacity-40 pointer-events-none floaty">
      <Leaf className="w-12 h-12 lg:w-20 lg:h-20 text-[#D4A017] rotate-45" strokeWidth={1} />
    </div>

    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
      <div className="grid lg:grid-cols-12 gap-6 lg:gap-14 items-center">
        {/* Left: text */}
        <div className="lg:col-span-7 text-left">
          <Reveal>
            <div className="chip chip-gold mb-5">
              <Sparkles className="w-3.5 h-3.5" />
              <span>Handcrafted · Made in India</span>
            </div>
          </Reveal>
          <Reveal delay={0.05}>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold text-[#143C18] leading-[1.05] tracking-tight">
              Pure A2 Cow Ghee &amp;
              <span className="block">Forest Honey</span>
              <span className="block text-[#2E7D32] italic font-serif-alt mt-1">
                from Nature to Your Home
              </span>
            </h1>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-6 text-base sm:text-lg text-[#5D4037] max-w-xl leading-relaxed">
              Rudrima brings you <Link to="/forest-honey" className="font-semibold text-[#1F5D25] underline-offset-2 hover:underline">100% natural raw forest honey</Link> and <Link to="/a2cow-ghee" className="font-semibold text-[#1F5D25] underline-offset-2 hover:underline">traditional Bilona A2 cow ghee</Link> — crafted with care and traditional wisdom, hygienically packed and delivered to your doorstep.
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="mt-8 grid grid-cols-1 sm:flex sm:flex-wrap gap-3">
              <Link to="/a2cow-ghee" data-testid={TESTIDS.heroOrderBtn} className="btn-primary justify-center !py-3.5">
                <ShoppingBag className="w-4 h-4" /> Shop A2 Cow Ghee – ₹999
              </Link>
              <Link to="/forest-honey" data-testid={TESTIDS.heroViewProducts} className="btn-outline justify-center !py-3.5">
                Shop Forest Honey – ₹599 <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
          </Reveal>
          <Reveal delay={0.2}>
            <div className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-4 text-sm">
              <div className="flex items-center gap-2 text-[#5D4037]">
                <ShieldCheck className="w-5 h-5 text-[#2E7D32]" /> <span className="font-semibold">100% Natural</span>
              </div>
              <div className="flex items-center gap-2 text-[#5D4037]">
                <Award className="w-5 h-5 text-[#D4A017]" /> <span className="font-semibold">Traditionally Made</span>
              </div>
              <div className="flex items-center gap-2 text-[#5D4037]">
                <Truck className="w-5 h-5 text-[#8D6E63]" /> <span className="font-semibold">All India Delivery</span>
              </div>
            </div>
          </Reveal>
        </div>

        {/* Right: product visuals */}
        <div className="lg:col-span-5 relative order-first lg:order-none">
          <Reveal delay={0.1}>
            <div className="relative aspect-[5/4] sm:aspect-[5/6] max-w-[420px] sm:max-w-[540px] mx-auto">
              {/* Circular backdrop */}
              <div className="absolute inset-6 rounded-full bg-gradient-to-br from-[#D4A017]/25 via-[#FFF8E7] to-[#2E7D32]/15 blur-2xl"></div>

              {/* Honey jar */}
              <motion.div
                initial={{ opacity: 0, x: -30, rotate: -6 }}
                animate={{ opacity: 1, x: 0, rotate: -4 }}
                transition={{ duration: 0.9, ease: "easeOut" }}
                className="absolute left-0 top-6 w-[62%] rounded-3xl overflow-hidden shadow-[0_30px_60px_-20px_rgba(20,60,24,0.4)] border-4 border-[#FFF8E7] floaty"
              >
                <img src={ASSETS.honeyLight} alt="Rudrima pure forest honey 500g jar, 100% natural and raw" width="600" height="600" className="w-full h-auto object-cover" loading="eager" fetchPriority="high" />
              </motion.div>

              {/* Ghee jar */}
              <motion.div
                initial={{ opacity: 0, x: 40, rotate: 8 }}
                animate={{ opacity: 1, x: 0, rotate: 5 }}
                transition={{ duration: 0.9, ease: "easeOut", delay: 0.15 }}
                className="absolute right-0 bottom-4 w-[58%] rounded-3xl overflow-hidden shadow-[0_30px_60px_-20px_rgba(20,60,24,0.4)] border-4 border-[#FFF8E7] floaty-slow"
              >
                <img src={ASSETS.gheeSweets} alt="Rudrima pure A2 cow ghee 500g, traditional bilona made" width="600" height="600" className="w-full h-auto object-cover" loading="eager" />
              </motion.div>

              {/* Floating badge */}
              <motion.div
                initial={{ opacity: 0, scale: 0.7 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: 0.6, type: "spring" }}
                className="absolute top-4 right-4 lg:top-2 lg:right-2 bg-white rounded-2xl px-4 py-3 shadow-xl border border-[#D4A017]/30 z-10"
              >
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-[#2E7D32] flex items-center justify-center">
                    <Check className="w-4 h-4 text-white" strokeWidth={3} />
                  </div>
                  <div className="leading-tight">
                    <div className="text-[10px] uppercase tracking-wider text-[#8D6E63] font-bold">Farm</div>
                    <div className="text-sm font-bold text-[#143C18]">Fresh</div>
                  </div>
                </div>
              </motion.div>

              {/* Rating badge */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.8 }}
                className="absolute -bottom-4 left-2 lg:left-8 bg-[#143C18] text-white rounded-2xl px-4 py-3 shadow-xl z-10"
              >
                <div className="flex items-center gap-2">
                  <ShieldCheck className="w-5 h-5 text-[#E8BE4A]" />
                  <div className="text-xs">
                    <span className="font-bold">Secure Shopify Checkout</span>
                    <span className="text-white/60 ml-1">· Ships across India</span>
                  </div>
                </div>
              </motion.div>
            </div>
          </Reveal>
        </div>
      </div>
    </div>
  </section>
);

/* -------------------- TRUST BADGES MARQUEE -------------------- */
const TrustMarquee = () => {
  const badges = [
    { icon: Leaf, label: "100% Natural" },
    { icon: ShieldCheck, label: "No Chemicals" },
    { icon: Flame, label: "Traditional Process" },
    { icon: Award, label: "Premium Quality" },
    { icon: Trees, label: "Farm Fresh" },
    { icon: Package, label: "Hygienically Packed" },
    { icon: Heart, label: "Made in India" },
    { icon: Sparkles, label: "Small Batches" },
  ];
  const items = [...badges, ...badges];
  return (
    <section className="py-8 bg-[#143C18] text-white overflow-hidden border-y border-[#D4A017]/30">
      <div className="marquee-track flex gap-14 whitespace-nowrap">
        {items.map((b, i) => (
          <div key={i} className="flex items-center gap-3 shrink-0">
            <b.icon className="w-5 h-5 text-[#D4A017]" strokeWidth={1.5} />
            <span className="text-sm sm:text-base font-semibold tracking-wide">{b.label}</span>
            <span className="text-[#D4A017] text-lg">✦</span>
          </div>
        ))}
      </div>
    </section>
  );
};

/* -------------------- PRODUCTS -------------------- */
const ProductCard = ({ product, reverse }) => (
  <Reveal>
    <div className="card-premium overflow-hidden">
      <div className={`grid lg:grid-cols-2 ${reverse ? "lg:[&>*:first-child]:order-2" : ""}`}>
        <div className="relative bg-gradient-to-br from-[#FBF4E1] to-[#F6EBCB] p-6 lg:p-10 flex items-center justify-center min-h-[320px]">
          <img
            src={product.image}
            alt={product.name}
            className="w-full max-w-[420px] rounded-2xl object-cover shadow-[0_25px_50px_-15px_rgba(20,60,24,0.35)]"
            loading="lazy"
          />
          <div className="absolute top-5 left-5 chip chip-gold shadow-sm">
            <Sparkles className="w-3 h-3" /> {product.tag}
          </div>
        </div>
        <div className="p-6 lg:p-10 flex flex-col justify-center">
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#8D6E63] font-bold mb-3">Rudrima Signature</div>
          <h3 className="font-display text-3xl lg:text-4xl font-bold text-[#143C18] leading-tight">
            {product.name}
          </h3>
          <p className="mt-3 text-[#5D4037] leading-relaxed">{product.desc}</p>
          <ul className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-2.5">
            {product.features.map((f) => (
              <li key={f} className="flex items-center gap-2 text-sm text-[#1A1614]">
                <span className="w-5 h-5 rounded-full bg-[#2E7D32]/10 flex items-center justify-center shrink-0">
                  <Check className="w-3 h-3 text-[#2E7D32]" strokeWidth={3} />
                </span>
                <span>{f}</span>
              </li>
            ))}
          </ul>

          <div className="mt-7 flex items-center justify-between gap-4 pt-6 border-t border-[#8D6E63]/15">
            <div>
              <div className="text-[11px] uppercase tracking-wider text-[#8D6E63] font-bold">{product.weight}</div>
              <div className="font-display text-3xl font-bold text-[#143C18]">
                ₹{product.price}
                <span className="text-sm font-body font-medium text-[#8D6E63] ml-2 line-through">₹{product.mrp}</span>
              </div>
            </div>
            <a
              href={waUrl(`Hi Rudrima! I'd like to order ${product.name} (${product.weight}) at ₹${product.price}.`)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={product.testid}
              className="btn-primary !py-3"
            >
              <ShoppingBag className="w-4 h-4" /> Buy Now
            </a>
          </div>
        </div>
      </div>
    </div>
  </Reveal>
);

const Products = () => {
  const HONEY_TITLE = process.env.REACT_APP_SHOPIFY_HONEY_TITLE || process.env.REACT_APP_SHOPIFY_PRODUCT_TITLE || "Pure Forest Honey - 500g | 100% Natural & Raw Honey";
  const GHEE_TITLE = process.env.REACT_APP_SHOPIFY_GHEE_TITLE || "Pure A2 Cow Ghee - 500g | Traditional Bilona Made";

  return (
    <section id="products" className="py-14 sm:py-20 lg:py-28 bg-[#FFF8E7]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
        <Reveal>
          <div className="text-center max-w-2xl mx-auto mb-14">
            <div className="chip mb-4"><Leaf className="w-3.5 h-3.5" /> Our Bestsellers</div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-[#143C18] tracking-tight">
              Nature&apos;s finest, <span className="italic font-serif-alt text-[#D4A017]">jarred with love</span>
            </h2>
            <p className="mt-4 text-[#5D4037]">Two heritage products. Zero shortcuts. Only what nature intended.</p>
          </div>
        </Reveal>
        <div className="space-y-10">
          <Reveal>
            <ShopifyProductCard
              title={HONEY_TITLE}
              features={["Raw Forest Honey", "Naturally Sweet", "No Added Sugar", "No Artificial Flavours", "Deep Amber Flavour"]}
              weightLabel="500 g"
              tag="Live on Shopify"
              notifyMsg="Hi Rudrima! Please notify me when Pure Forest Honey (500g) is back in stock."
              buyTestId={TESTIDS.productHoneyBuy}
              addTestId="shopify-honey-add-to-cart-btn"
              cardTestId="shopify-honey-card"
              detailsTo="/forest-honey"
              detailsLabel="Explore Forest Honey"
              reverse={false}
            />
          </Reveal>
          <Reveal>
            <ShopifyProductCard
              title={GHEE_TITLE}
              features={["Bilona Churned", "A2 Cow Milk", "Rich Aroma", "Traditional Method", "Everyday Cooking"]}
              weightLabel="500 g"
              tag="Live on Shopify"
              notifyMsg="Hi Rudrima! Please notify me when Pure A2 Cow Ghee (500g) is back in stock."
              buyTestId={TESTIDS.productGheeBuy}
              addTestId="shopify-ghee-add-to-cart-btn"
              cardTestId="shopify-ghee-card"
              detailsTo="/a2cow-ghee"
              detailsLabel="Explore A2 Cow Ghee"
              reverse={true}
            />
          </Reveal>
          <ComboOfferCard />
        </div>
      </div>
    </section>
  );
};

/* -------------------- WHY CHOOSE -------------------- */
const WhyChoose = () => {
  const items = [
    { icon: Leaf, title: "Premium Ingredients", desc: "Sourced from trusted farms & pristine forests across India." },
    { icon: Sun, title: "100% Organic", desc: "No chemicals, no adulteration — just what nature grows." },
    { icon: Flame, title: "Traditional Production", desc: "Bilona churning & cold-extraction honour ancient wisdom." },
    { icon: ShieldCheck, title: "No Preservatives", desc: "Pure, unrefined and shelf-stable the way it should be." },
    { icon: Heart, title: "Rich Nutrition", desc: "Full of natural enzymes, vitamins and healthy fats." },
    { icon: Award, title: "Customer Satisfaction", desc: "Loved by 2,000+ families. Reviewed with 4.9 stars." },
  ];
  return (
    <section id="why" className="py-14 sm:py-20 lg:py-28 bg-cream-radial relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 relative">
        <Reveal>
          <div className="text-center max-w-2xl mx-auto mb-14">
            <div className="chip chip-gold mb-4"><Sparkles className="w-3.5 h-3.5" /> Why Rudrima</div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-[#143C18]">
              A promise you can <span className="italic font-serif-alt text-[#2E7D32]">taste</span>
            </h2>
          </div>
        </Reveal>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-7">
          {items.map((it, i) => (
            <Reveal key={it.title} delay={i * 0.05}>
              <div className="group h-full p-7 lg:p-8 rounded-3xl bg-white/70 backdrop-blur-sm border border-[#8D6E63]/12 hover:border-[#2E7D32]/40 hover:bg-white transition-all duration-500 hover:-translate-y-1.5 shadow-[0_10px_30px_-20px_rgba(20,60,24,0.35)]">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#2E7D32] to-[#1F5D25] flex items-center justify-center mb-5 shadow-lg shadow-[#2E7D32]/25 group-hover:scale-110 transition-transform duration-500">
                  <it.icon className="w-7 h-7 text-[#FFF8E7]" strokeWidth={1.5} />
                </div>
                <h3 className="font-display text-2xl font-bold text-[#143C18] mb-2">{it.title}</h3>
                <p className="text-[#5D4037] leading-relaxed text-[15px]">{it.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

/* -------------------- BENEFITS -------------------- */
const ComboBuyButton = () => {
  const [busy, setBusy] = useState(false);
  const buyCombo = async () => {
    setBusy(true);
    try {
      const [honey, ghee] = await Promise.all([
        fetchProductByTitle(process.env.REACT_APP_SHOPIFY_HONEY_TITLE),
        fetchProductByTitle(process.env.REACT_APP_SHOPIFY_GHEE_TITLE),
      ]);
      await addLineToSharedCart(honey.firstVariant.id, 1);
      let cart = await addLineToSharedCart(ghee.firstVariant.id, 1);
      if (process.env.REACT_APP_SHOPIFY_COMBO_CODE) cart = (await applyDiscountCode(process.env.REACT_APP_SHOPIFY_COMBO_CODE)) || cart;
      window.location.assign(cart.checkoutUrl);
    } catch (e) {
      toast.error(e.message || "Unable to start checkout. Please try again.");
      setBusy(false);
    }
  };
  return (
    <button onClick={buyCombo} disabled={busy} data-testid="combo-buy-btn" className="btn-gold disabled:opacity-60">
      <ShoppingBag className="w-4 h-4" /> {busy ? "Preparing checkout…" : "Order the Combo"}
    </button>
  );
};

const Benefits = () => (
  <section className="py-14 sm:py-20 lg:py-28 bg-[#143C18] text-white relative overflow-hidden">
    <div className="absolute top-10 right-10 opacity-10 pointer-events-none">
      <Leaf className="w-64 h-64 text-[#D4A017]" strokeWidth={0.5} />
    </div>
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 relative">
      <Reveal>
        <div className="max-w-2xl mb-14">
          <div className="chip chip-gold mb-4"><Heart className="w-3.5 h-3.5" /> Wholesome Benefits</div>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight">
            Everyday goodness,
            <span className="block italic font-serif-alt text-[#E8BE4A]">nature&apos;s way</span>
          </h2>
        </div>
      </Reveal>

      <div className="grid lg:grid-cols-2 gap-8 lg:gap-14 items-center">
        <Reveal>
          <div className="relative">
            <div className="grid grid-cols-2 gap-4">
              <img src={ASSETS.honeyForest} alt="Forest Honey" className="rounded-3xl w-full h-64 sm:h-80 object-cover shadow-2xl" loading="lazy" />
              <img src={ASSETS.gheeSweets} alt="Cow Ghee" className="rounded-3xl w-full h-64 sm:h-80 object-cover shadow-2xl mt-8" loading="lazy" />
            </div>
          </div>
        </Reveal>

        <div className="space-y-6">
          <Reveal delay={0.1}>
            <div className="p-7 rounded-3xl bg-white/5 border border-white/10 backdrop-blur-sm">
              <div className="flex items-center gap-3 mb-4">
                <Droplets className="w-6 h-6 text-[#D4A017]" />
                <h3 className="font-display text-2xl font-bold">Forest Honey</h3>
              </div>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-y-2 gap-x-4 text-white/90">
                {["Supports immunity", "Natural energy boost", "Rich in antioxidants", "Pure forest nectar"].map((f) => (
                  <li key={f} className="flex items-center gap-2 text-sm"><Check className="w-4 h-4 text-[#D4A017]" strokeWidth={3} /> {f}</li>
                ))}
              </ul>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="p-7 rounded-3xl bg-white/5 border border-white/10 backdrop-blur-sm">
              <div className="flex items-center gap-3 mb-4">
                <Flame className="w-6 h-6 text-[#D4A017]" />
                <h3 className="font-display text-2xl font-bold">A2 Cow Ghee</h3>
              </div>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-y-2 gap-x-4 text-white/90">
                {["Rich nutrition", "Healthy fats", "Traditional preparation", "Great aroma & taste"].map((f) => (
                  <li key={f} className="flex items-center gap-2 text-sm"><Check className="w-4 h-4 text-[#D4A017]" strokeWidth={3} /> {f}</li>
                ))}
              </ul>
            </div>
          </Reveal>
          <Reveal delay={0.2}>
            <ComboBuyButton />
          </Reveal>
        </div>
      </div>
    </div>
  </section>
);

/* -------------------- REVIEWS -------------------- */
const Reviews = () => {
  const reviews = REVIEWS;
  const colors = REVIEW_COLORS;
  return (
    <section id="reviews" className="py-14 sm:py-20 lg:py-28 bg-[#FFF8E7]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
        <Reveal>
          <div className="text-center max-w-2xl mx-auto mb-14">
            <div className="chip mb-4"><Star className="w-3.5 h-3.5 fill-current" /> 4.9/5 Rating</div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-[#143C18]">
              Loved by <span className="italic font-serif-alt text-[#D4A017]">2000+ families</span>
            </h2>
            <p className="mt-4 text-[#5D4037]">Real reviews from customers who made Rudrima part of their daily kitchen.</p>
          </div>
        </Reveal>

        {/* Mobile carousel via horizontal scroll */}
        <div className="lg:hidden overflow-x-auto no-scrollbar -mx-4 px-4">
          <div className="flex gap-4 pb-4" style={{ scrollSnapType: "x mandatory" }}>
            {reviews.map((r, i) => (
              <ReviewCard key={i} r={r} color={colors[i % colors.length]} className="min-w-[85%] scroll-snap-align-start" />
            ))}
          </div>
        </div>

        {/* Desktop grid */}
        <div className="hidden lg:grid grid-cols-3 gap-6">
          {reviews.map((r, i) => (
            <Reveal key={i} delay={i * 0.05}>
              <ReviewCard r={r} color={colors[i % colors.length]} />
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

const ReviewCard = ({ r, color, className = "" }) => (
  <div className={`card-premium p-7 h-full flex flex-col ${className}`} style={{ scrollSnapAlign: "start" }}>
    <div className="flex gap-1 mb-4">
      {[...Array(r.rating)].map((_, i) => (
        <Star key={i} className="w-4 h-4 fill-[#D4A017] text-[#D4A017]" />
      ))}
    </div>
    <p className="text-[#1A1614] leading-relaxed flex-1 italic font-serif-alt text-lg">&ldquo;{r.text}&rdquo;</p>
    <div className="mt-5 pt-5 border-t border-[#8D6E63]/15 flex items-center gap-3">
      <div className="w-11 h-11 rounded-full flex items-center justify-center text-white font-bold" style={{ background: color }}>
        {r.initial}
      </div>
      <div>
        <div className="font-bold text-[#143C18]">{r.name}</div>
        <div className="text-xs text-[#8D6E63] flex items-center gap-1"><MapPin className="w-3 h-3" /> {r.city}</div>
      </div>
    </div>
  </div>
);

/* -------------------- PROMISE -------------------- */
const OurPromise = () => {
  const promises = [
    { icon: Leaf, title: "100% Natural" },
    { icon: ShieldCheck, title: "Quality Tested" },
    { icon: Package, title: "Fresh Packaging" },
    { icon: Truck, title: "Fast Delivery" },
    { icon: Heart, title: "Customer Support" },
  ];
  return (
    <section className="py-12 sm:py-16 lg:py-20 bg-[#FBF4E1] border-y border-[#8D6E63]/10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
        <Reveal>
          <div className="text-center mb-10">
            <div className="chip mb-3"><Award className="w-3.5 h-3.5" /> Our Promise</div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-[#143C18]">Five things we <span className="italic font-serif-alt text-[#2E7D32]">never compromise on</span></h2>
          </div>
        </Reveal>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
          {promises.map((p, i) => (
            <Reveal key={p.title} delay={i * 0.05}>
              <div className="flex flex-col items-center text-center p-5 rounded-2xl bg-white/60 border border-[#8D6E63]/12 hover:bg-white transition-all">
                <div className="w-14 h-14 rounded-full bg-[#2E7D32]/10 border border-[#2E7D32]/30 flex items-center justify-center mb-3">
                  <p.icon className="w-6 h-6 text-[#2E7D32]" strokeWidth={1.5} />
                </div>
                <div className="font-semibold text-[#143C18]">{p.title}</div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

/* -------------------- FAQ -------------------- */
const FAQ = () => {
  const faqs = [
    { q: "Is the honey 100% pure?", a: "Yes. Our forest honey is raw, unprocessed and harvested directly from wild bee colonies. It contains no added sugar, no preservatives and is lab-tested for purity before packing." },
    { q: "Is the ghee made traditionally?", a: "Absolutely. We follow the ancient Bilona method — hand-churning curd from A2 grass-fed cow milk into butter, then slow-cooking it into golden ghee in small batches." },
    { q: "How should I store the products?", a: "Store both honey and ghee at room temperature away from direct sunlight. Always use a clean, dry spoon. No refrigeration needed. Once opened, use within 6 months for the best flavour." },
    { q: "Do you deliver across India?", a: "Yes! We ship pan-India through trusted courier partners. Orders are typically dispatched within 24 hours and delivered in 3–6 business days." },
    { q: "How can I place an order?", a: "The fastest way is to tap any Order Now button on this page — it opens WhatsApp with a pre-filled message. You can also fill the contact form below or call us at " + BRAND.whatsappDisplay + "." },
  ];
  return (
    <section id="faq" className="py-14 sm:py-20 lg:py-28 bg-[#FFF8E7]" data-testid={TESTIDS.faqAccordion}>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-10">
        <Reveal>
          <div className="text-center mb-12">
            <div className="chip mb-3"><MessageCircle className="w-3.5 h-3.5" /> FAQ</div>
            <h2 className="font-display text-4xl sm:text-5xl font-bold text-[#143C18]">Frequently <span className="italic font-serif-alt text-[#D4A017]">asked</span></h2>
          </div>
        </Reveal>
        <div className="space-y-3">
          {faqs.map((f, i) => (
            <Reveal key={i} delay={i * 0.05}>
              <details className="group rounded-2xl bg-white border border-[#8D6E63]/15 open:border-[#2E7D32]/40 open:shadow-lg transition-all overflow-hidden">
                <summary className="flex items-center justify-between gap-4 p-5 sm:p-6 cursor-pointer">
                  <span className="font-display text-lg sm:text-xl font-semibold text-[#143C18]">{f.q}</span>
                  <Plus className="acc-icon w-5 h-5 text-[#2E7D32] transition-transform shrink-0" />
                </summary>
                <div className="px-5 sm:px-6 pb-6 text-[#5D4037] leading-relaxed">{f.a}</div>
              </details>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

/* -------------------- CTA BANNER -------------------- */
const CTA = () => (
  <section className="py-14 sm:py-20 lg:py-28 relative overflow-hidden bg-forest-deep">
    <div className="absolute inset-0 opacity-30 pointer-events-none">
      <div className="absolute top-10 left-10"><Leaf className="w-32 h-32 text-[#D4A017]" strokeWidth={0.5} /></div>
      <div className="absolute bottom-10 right-10 rotate-45"><Leaf className="w-40 h-40 text-[#E8BE4A]" strokeWidth={0.5} /></div>
    </div>
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-10 relative text-center">
      <Reveal>
        <div className="chip chip-gold mb-5"><Sparkles className="w-3.5 h-3.5" /> Limited Batches</div>
        <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight">
          Bring home the <span className="italic font-serif-alt text-[#E8BE4A]">purity of nature</span>
        </h2>
        <p className="mt-5 text-white/85 text-lg max-w-2xl mx-auto">
          Choose Rudrima for authentic organic products crafted with care, tradition and complete transparency.
        </p>
        <div className="mt-8 flex flex-wrap justify-center gap-3">
          <a href="#products" data-testid={TESTIDS.ctaOrderBtn} className="btn-gold">
            <ShoppingBag className="w-4 h-4" /> Shop Now
          </a>
          <Link to="/contact-us" data-testid={TESTIDS.ctaContactBtn} className="btn-outline !border-white !text-white hover:!bg-white hover:!text-[#143C18]">
            Contact Us <ChevronRight className="w-4 h-4" />
          </Link>
        </div>
      </Reveal>
    </div>
  </section>
);

/* -------------------- CONTACT -------------------- */
const Contact = () => {
  const [form, setForm] = useState({ name: "", phone: "", email: "", product: "Organic Forest Honey", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Please provide your name and phone number.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSent(true);
      toast.success("Thank you! We'll reach out shortly.");
      setForm({ name: "", phone: "", email: "", product: "Organic Forest Honey", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please WhatsApp us instead.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-14 sm:py-20 lg:py-28 bg-cream-radial">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-10">
        <div className="grid lg:grid-cols-5 gap-10 items-start">
          <Reveal className="lg:col-span-2">
            <div className="chip mb-4"><Send className="w-3.5 h-3.5" /> Talk to us</div>
            <h2 className="font-display text-4xl sm:text-5xl font-bold text-[#143C18] leading-tight">
              Have a question?
              <span className="block italic font-serif-alt text-[#2E7D32] mt-1">We&apos;d love to hear.</span>
            </h2>
            <p className="mt-4 text-[#5D4037]">Reach out via WhatsApp for the fastest response, or drop a message below and our team will get back within 24 hours.</p>

            <div className="mt-8 space-y-4">
              <a href={waUrl()} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 p-4 rounded-2xl bg-white border border-[#8D6E63]/15 hover:border-[#2E7D32]/40 transition-all group">
                <div className="w-11 h-11 rounded-full bg-[#25D366] flex items-center justify-center shrink-0">
                  <MessageCircle className="w-5 h-5 text-white" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-[#8D6E63] font-bold">WhatsApp</div>
                  <div className="font-semibold text-[#143C18]">{BRAND.whatsappDisplay}</div>
                </div>
              </a>
              <a href={`mailto:${BRAND.email}`} className="flex items-center gap-3 p-4 rounded-2xl bg-white border border-[#8D6E63]/15 hover:border-[#2E7D32]/40 transition-all">
                <div className="w-11 h-11 rounded-full bg-[#2E7D32] flex items-center justify-center shrink-0">
                  <Mail className="w-5 h-5 text-white" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-[#8D6E63] font-bold">Email</div>
                  <div className="font-semibold text-[#143C18]">{BRAND.email}</div>
                </div>
              </a>
            </div>
          </Reveal>

          <Reveal delay={0.1} className="lg:col-span-3">
            <form onSubmit={onSubmit} data-testid={TESTIDS.contactForm} className="card-premium p-6 sm:p-8">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs uppercase tracking-widest text-[#8D6E63] font-bold mb-2 block">Full Name *</label>
                  <input
                    name="name"
                    value={form.name}
                    onChange={onChange}
                    required
                    data-testid={TESTIDS.contactName}
                    className="w-full px-4 py-3 rounded-xl bg-[#FBF4E1] border border-[#8D6E63]/15 focus:border-[#2E7D32] focus:ring-2 focus:ring-[#2E7D32]/20 outline-none transition"
                    placeholder="Your name"
                  />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-[#8D6E63] font-bold mb-2 block">Phone *</label>
                  <input
                    name="phone"
                    value={form.phone}
                    onChange={onChange}
                    required
                    data-testid={TESTIDS.contactPhone}
                    className="w-full px-4 py-3 rounded-xl bg-[#FBF4E1] border border-[#8D6E63]/15 focus:border-[#2E7D32] focus:ring-2 focus:ring-[#2E7D32]/20 outline-none transition"
                    placeholder="+91 98xxxxxxxx"
                  />
                </div>
              </div>
              <div className="mt-4 grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs uppercase tracking-widest text-[#8D6E63] font-bold mb-2 block">Email</label>
                  <input
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={onChange}
                    data-testid={TESTIDS.contactEmail}
                    className="w-full px-4 py-3 rounded-xl bg-[#FBF4E1] border border-[#8D6E63]/15 focus:border-[#2E7D32] focus:ring-2 focus:ring-[#2E7D32]/20 outline-none transition"
                    placeholder="you@example.com"
                  />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-[#8D6E63] font-bold mb-2 block">Product Interest</label>
                  <select
                    name="product"
                    value={form.product}
                    onChange={onChange}
                    className="w-full px-4 py-3 rounded-xl bg-[#FBF4E1] border border-[#8D6E63]/15 focus:border-[#2E7D32] focus:ring-2 focus:ring-[#2E7D32]/20 outline-none transition"
                  >
                    <option>Organic Forest Honey</option>
                    <option>Pure A2 Cow Ghee</option>
                    <option>Both / Combo</option>
                    <option>Bulk / Wholesale</option>
                  </select>
                </div>
              </div>
              <div className="mt-4">
                <label className="text-xs uppercase tracking-widest text-[#8D6E63] font-bold mb-2 block">Message</label>
                <textarea
                  name="message"
                  value={form.message}
                  onChange={onChange}
                  rows={4}
                  data-testid={TESTIDS.contactMessage}
                  className="w-full px-4 py-3 rounded-xl bg-[#FBF4E1] border border-[#8D6E63]/15 focus:border-[#2E7D32] focus:ring-2 focus:ring-[#2E7D32]/20 outline-none transition resize-none"
                  placeholder="Tell us about your order or ask us anything…"
                />
              </div>

              <div className="mt-6 flex flex-col sm:flex-row items-center gap-4 justify-between">
                <div className="text-xs text-[#8D6E63]">By submitting, you agree to be contacted by Rudrima.</div>
                <button type="submit" disabled={submitting} data-testid={TESTIDS.contactSubmit} className="btn-primary w-full sm:w-auto justify-center">
                  {submitting ? "Sending…" : (<><Send className="w-4 h-4" /> Send Message</>)}
                </button>
              </div>
              {sent && (
                <div data-testid={TESTIDS.contactSuccess} className="mt-4 p-4 rounded-xl bg-[#2E7D32]/10 border border-[#2E7D32]/30 text-[#1F5D25] text-sm font-medium flex items-center gap-2">
                  <Check className="w-4 h-4" /> Thanks! Your message reached us. We&apos;ll respond via WhatsApp / Email soon.
                </div>
              )}
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
};


const MobileStickyCTA = () => (
  <div className="fixed bottom-0 left-0 right-0 z-40 lg:hidden bg-[#FFF8E7]/95 backdrop-blur-xl border-t border-[#8D6E63]/15 px-3 pt-2.5 shadow-[0_-10px_30px_-15px_rgba(20,60,24,0.35)]" style={{ paddingBottom: "calc(0.625rem + env(safe-area-inset-bottom))" }} data-testid="home-sticky-bar">
    <div className="grid grid-cols-2 gap-2">
      <Link to="/a2cow-ghee" data-testid={TESTIDS.mobileStickyOrder} className="btn-primary justify-center !py-3 !px-3 text-[15px]">
        <ShoppingBag className="w-4 h-4" /> Ghee · ₹999
      </Link>
      <Link to="/forest-honey" data-testid="mobile-sticky-honey-btn" className="btn-gold justify-center !py-3 !px-3 text-[15px]">
        <ShoppingBag className="w-4 h-4" /> Honey · ₹599
      </Link>
    </div>
  </div>
);

/* -------------------- APP -------------------- */
const HOME_SEO = {
  title: "Rudrima – Pure A2 Cow Ghee & Natural Forest Honey",
  description: "Rudrima sells Pure A2 Cow Ghee (500g, traditional Bilona made) and Pure Forest Honey (500g, 100% natural & raw). Hygienically packed, secure Shopify checkout, delivered across India.",
  path: "/",
  image: ASSETS.honeyLight,
  jsonLd: graph(
    webPageLd({ path: "/", name: "Rudrima – Pure A2 Cow Ghee & Natural Forest Honey", description: "Pure A2 cow ghee and natural forest honey by Rudrima.", type: "CollectionPage" }),
    {
      "@type": "ItemList",
      name: "Rudrima Products",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Pure A2 Cow Ghee - 500g | Traditional Bilona Made", url: "https://rudrima.com/a2cow-ghee" },
        { "@type": "ListItem", position: 2, name: "Pure Forest Honey - 500g | 100% Natural & Raw Honey", url: "https://rudrima.com/forest-honey" },
      ],
    },
  ),
};

const HomePage = () => {
  useSeo(HOME_SEO);
  return (
  <>
    <main>
      <Hero />
      <TrustMarquee />
      <Products />
      <WhyChoose />
      <Benefits />
      <Reviews />
      <OurPromise />
      <FAQ />
      <CTA />
      <Contact />
    </main>
    <Footer />
    <FloatingWhatsApp />
    <MobileStickyCTA />
  </>
  );
};

function App() {
  return (
    <div className="min-h-screen bg-[#FFF8E7]">
      <Toaster position="top-center" richColors closeButton />
      <ScrollManager />
      <Header />
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/contact-us" element={<ContactPage />} />
        <Route path="/track-order" element={<TrackOrderPage />} />
        <Route path="/a2cow-ghee" element={<ProductLanding key="ghee" config={GHEE_PAGE} />} />
        <Route path="/forest-honey" element={<ProductLanding key="honey" config={HONEY_PAGE} />} />
        {POLICIES.map((p) => (
          <Route key={p.slug} path={p.slug} element={<PolicyPage key={p.slug} policy={p} />} />
        ))}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </div>
  );
}

export default App;
